import React from "react";
import { useTranslation } from "react-i18next";
import type { TransformedAddress } from "../../cart/types/cartApiTypes";
import type { TrCountry, ThemeVariables } from "../../../types";
import type { TrCity } from "../../checkout/types/checkoutApiTypes";
import BacsDetailsDisplay from "./BacsDetailsDisplay";

// Helper function to find city name by code
const findCityNameByCode = (
  code: string | undefined,
  cities: TrCity[] | undefined
): string => {
  if (!code || !cities) return code || ""; // Return code or empty if no data
  const city = cities.find((c) => c.wcCode === code);
  return city?.name || code; // Return name or fallback to code
};

interface AddressDisplayProps {
  address: TransformedAddress;
  type: "billing" | "shipping";
  cities?: TrCity[];
}

const AddressDisplay: React.FC<AddressDisplayProps> = ({
  address,
  type,
  cities,
}) => {
  const { t } = useTranslation();
  const trCountry = (window.themeVariables as ThemeVariables | undefined)
    ?.trCountry as TrCountry | undefined;

  const displayCountry =
    trCountry && address.country === trCountry.wcCode
      ? trCountry.name
      : address.country || "";

  const displayState = findCityNameByCode(address.state, cities);

  return (
    <div className={`address-block address-${type}`}>
      <h3>
        {type === "billing"
          ? t("checkout.billingAddress", "Billing Address")
          : t("checkout.shippingAddress", "Shipping Address")}
      </h3>
      <p>
        {address.firstName} {address.lastName}
        <br />
        {address.company && (
          <>
            {address.company}
            <br />
          </>
        )}
        {address.address1}
        <br />
        {address.address2 && (
          <>
            {address.address2}
            <br />
          </>
        )}
        {address.city}, {displayState} {address.postcode}
        <br />
        {displayCountry}
        {address.phone && (
          <>
            <br />
            {t("checkout.fields.phone", "Phone")}: {address.phone}
          </>
        )}
        {type === "billing" && address.email && (
          <>
            <br />
            {t("checkout.fields.email", "Email")}: {address.email}
          </>
        )}
      </p>
    </div>
  );
};

// --- OrderStatusInfo Internal Component (Unchanged) ---
interface OrderStatusInfoProps {
  orderId: number;
  status: string;
}
const OrderStatusInfo: React.FC<OrderStatusInfoProps> = ({
  orderId,
  status,
}) => {
  const { t } = useTranslation();
  return (
    <div className="order-status-info">
      <h2>{t("order.confirmationTitle", "Order Received")}</h2>
      <p>
        {t(
          "order.confirmationMessage",
          "Thank you. Your order (ID: {{orderId}}) has been received.",
          { orderId }
        )}
      </p>
      <p>
        <strong>{t("order.statusLabel", "Status:")}</strong>{" "}
        <span className={`order-status status-${status}`}>
          {t(`order.status.${status}`, status)}
        </span>
      </p>
    </div>
  );
};
// --- End OrderStatusInfo ---

// Props for the main component
interface OrderDetailsDisplayProps {
  orderId: number;
  status: string;
  billingAddress: TransformedAddress;
  shippingAddress: TransformedAddress;
  cities?: TrCity[];
  orderMethod?: string | null; // Ensure this prop is defined
}

const OrderDetailsDisplay: React.FC<OrderDetailsDisplayProps> = ({
  orderId,
  status,
  billingAddress,
  shippingAddress,
  cities,
  orderMethod, // Use the prop
}) => {
  const isShippingSameAsBilling =
    JSON.stringify(billingAddress) === JSON.stringify(shippingAddress);

  return (
    <div className="order-summary-details">
      {/* Use the new OrderStatusInfo component */}
      <OrderStatusInfo orderId={orderId} status={status} />

      {/* Conditionally render BACS details based on the orderMethod prop */}
      {orderMethod === "bacs" && <BacsDetailsDisplay />}

      <div className="address-section">
        <AddressDisplay
          address={billingAddress}
          type="billing"
          cities={cities}
        />
        {!isShippingSameAsBilling && (
          <AddressDisplay
            address={shippingAddress}
            type="shipping"
            cities={cities}
          />
        )}
      </div>
    </div>
  );
};

export default OrderDetailsDisplay;
